import {
  computed,
  Ref,
} from "vue";

import {
  type SkEquipmentTypeConversion,
  useSkEquipmentTypeConversionsQuery,
} from "gql/api";

import {
  UIMaterial,
} from "@/components/quotes/types";

import * as helpers from "@/utils/quoteHelpers";


// used in this file only
const descriptionChecker = (description: string | undefined) => {
  if (!description || description === "") {
    return undefined;
  }

  if (description.includes("PMP")) {
    return helpers.PUMP;
  } else if (description.includes("CABLE") || description.includes("CBL")) {
    return helpers.CABLE;
  } else if (description.includes("MTR")) {
    return helpers.MOTOR;
  } else if (description.includes("MLE")) {
    return helpers.MOTOR_LEAD_EXTENSION;
  } else {
    return null;
  }
};


export const useEquipmentTypeConversions = (selectedMaterial: Ref<UIMaterial | null>) => {
  // Create mapping between product hierarchy constants and spyglass LineItemType values
  const typeMap = {
    [helpers.PUMP]: helpers.LineItemType.PUMP,
    [helpers.GAS_SEP]: helpers.LineItemType.GAS_SEPARATOR,
    [helpers.INTAKE]: helpers.LineItemType.INTAKE,
    [helpers.SEAL]: helpers.LineItemType.SEAL,
    [helpers.MOTOR]: helpers.LineItemType.MOTOR,
    [helpers.SENSOR]: helpers.LineItemType.SENSOR,
    [helpers.CABLE]: helpers.LineItemType.CABLE,
    [helpers.MOTOR_LEAD_EXTENSION]: helpers.LineItemType.MLE,
    [helpers.CAP_TUBE]: helpers.LineItemType.CAPILLARY_TUBE,
    [helpers.TRANSFORMER]: helpers.LineItemType.TRANSFORMER,
    [helpers.FILTERS]: helpers.LineItemType.FILTERS,
    [helpers.PROTECTORS_SEALS]: helpers.LineItemType.PROTECTORS_SEALS,
    [helpers.SINE_WAVE_FILTER]: helpers.LineItemType.SINE_WAVE_FILTER,
    [helpers.MISC]: helpers.LineItemType.MISCELLANEOUS,
    [helpers.SWITCHBOARD]: helpers.LineItemType.SWITCHBOARD,
    [helpers.VSD]: helpers.LineItemType.VSD,
  } as { [key: string]: helpers.LineItemType };

  // Equipment Type Conversion Data Fetching
  const {
    data: equipmentTypeConversions,
    executeQuery: fetchSKEquipmentTypeConversions
  } = useSkEquipmentTypeConversionsQuery({
    variables: computed(() => {
      return {
        summitQueryString: ""
      };
    })
  });

  fetchSKEquipmentTypeConversions();

  const searchProductNumber = computed(() => {
    return selectedMaterial?.value?.prod_hierarchy;
  });
  const searchDescription = computed(() => {
    return selectedMaterial?.value?.description;
  });

  const test = () => {};

  //const foundEtc = computed(() => {
  const foundEtc = computed((): SkEquipmentTypeConversion | null | undefined => {
    if (!searchProductNumber?.value) {
      return null;
    }

    // Search for material prod hierarchy number inside equipment type conversions.
    let localEtc = null;

    const etcs = equipmentTypeConversions?.value?.getSKEquipmentTypeConversions || [];
    etcs.find((type_conversion) => {
      if (
        type_conversion?.hierarchy_numbers === null ||
        type_conversion?.hierarchy_numbers === undefined
      ) {
        return false
      }

      return type_conversion?.hierarchy_numbers.forEach((hierarchy_num) => {
        if (hierarchy_num) {
          const match = hierarchy_num.startsWith(searchProductNumber?.value || "");

          if (match === true) {
            localEtc = type_conversion;
          }
        }
      });
    });

    if (!localEtc) {
      // check description as fallback
      const category = descriptionChecker(searchDescription.value || "");

      if (category) {
        return etcs.find((etc) => {
          return (
            helpers.notNull(etc) ? etc.equipment_type : "no type"
          ) === category
        });
      } else {
        return null;
      }
    }

    return localEtc;
  });

  const materialEquipmentType = computed(() => {
    // returns: Product Hierarchy Line Item Type
    return foundEtc?.value?.equipment_type;
  });
  const computedLineItemType = computed((): helpers.LineItemType | "" => {
    // For reactively determining line item type at material selection
    // returns: Spyglass Line Item Type
    if (!selectedMaterial.value) {
      return "";
    }

    if (
      !foundEtc?.value ||
      foundEtc?.value?.equipment_type === undefined ||
      foundEtc?.value?.equipment_type === ""
    ) {
      return helpers.LineItemType.UNCATEGORIZED;
    }

    type TypeMapKey = keyof typeof typeMap;
    const equipment_type = foundEtc?.value?.equipment_type;
    if (equipment_type) {
      return typeMap[equipment_type as TypeMapKey];
    } else {
      return helpers.LineItemType.UNCATEGORIZED;
    }
  });

  return {
    foundEtc,
    materialEquipmentType,
    computedLineItemType,
    equipmentTypeConversions,
  };
}
